import { createContext, useContext, useState, ReactNode } from 'react';

export interface OnboardingCompanyData {
  companyId: string;
  companyName: string;
  ownerEmail: string;
  masterKey: string;
  firstEmployeeName: string;
  firstEmployeeEmail: string;
}

export interface OnboardingSeatData {
  activationToken: string;
  seatId: string;
}

export type OnboardingStep = 'setup' | 'masterKey' | 'qrCode' | 'complete';

interface OnboardingContextType {
  companyData: OnboardingCompanyData | null;
  seatData: OnboardingSeatData | null;
  currentStep: OnboardingStep | null;
  isOnboarding: boolean;
  startOnboarding: (data: OnboardingCompanyData) => void;
  setSeatData: (data: OnboardingSeatData) => void;
  setStep: (step: OnboardingStep) => void;
  completeOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [companyData, setCompanyData] = useState<OnboardingCompanyData | null>(null);
  const [seatData, setSeatDataState] = useState<OnboardingSeatData | null>(null);
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);

  const isOnboarding = companyData !== null && currentStep !== null && currentStep !== 'complete';

  const startOnboarding = (data: OnboardingCompanyData) => {
    setCompanyData(data);
    setCurrentStep('setup');
  };

  const setSeatData = (data: OnboardingSeatData) => {
    setSeatDataState(data);
  };

  const setStep = (step: OnboardingStep) => {
    setCurrentStep(step);
  };

  const completeOnboarding = () => {
    setCompanyData(null);
    setSeatDataState(null);
    setCurrentStep(null);
  };

  return (
    <OnboardingContext.Provider
      value={{
        companyData,
        seatData,
        currentStep,
        isOnboarding,
        startOnboarding,
        setSeatData,
        setStep,
        completeOnboarding
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
